import { useState, useEffect } from 'react';
import type {
  EngineGapPattern,
  Gap,
  GapListResponse,
  GapSeverity,
  GapType,
  ReliabilityTier,
} from '../types/index.js';
import { ENGINE_LABELS } from '../types/index.js';
import { apiGet } from '../lib/api.js';

interface GapSummaryProps {
  tenantId: string | null;
  brandId: string | null;
  /** Worst reliability tier across the brand's engines. When 'directional', the
   *  reassuring "all clear" is qualified so a thin sample isn't read as validated. */
  headlineTier?: ReliabilityTier;
}

// Spec 251: Kridt's tone edge (3px, the status colour) and a toned chip —
// severity is a status. The row edge was 4px in four Tailwind hues and the
// badge a 4px-radius tinted block; 250.A.5 found the radius on seeded data.
const SEVERITY_BORDER: Record<GapSeverity, string> = {
  critical: 'border-l-[var(--hub-color-danger)]',
  high: 'border-l-[var(--hub-color-warning)]',
  medium: 'border-l-[var(--hub-color-warning)]',
  low: 'border-l-[var(--hub-color-line-strong)]',
};

const SEVERITY_BADGE: Record<GapSeverity, { tone: string; label: string }> = {
  critical: { tone: 'hub-chip--fail', label: 'Critical' },
  high: { tone: 'hub-chip--warn', label: 'High' },
  medium: { tone: '', label: 'Medium' },
  low: { tone: '', label: 'Low' },
};

// Plain-language labels for every gap type the detector emits, plus the legacy
// names kept for old stored rows. Previously this map was keyed entirely on
// names the backend never emits, so the real types (`engine_gap` and friends)
// resolved to `undefined` and the badge fell through to the raw slug.
const TYPE_LABELS: Record<GapType, string> = {
  // Emitted today:
  engine_gap: 'Engine gap',
  sentiment_gap: 'Negative sentiment',
  sov_decline: 'Share of voice decline',
  position_gap: 'Weak position',
  category_gap: 'Topic gap',
  competitor_domain_dominance: 'Competitor dominates sources',
  retrieval_without_citation: 'Retrieved but not cited',
  missing_authority_source: 'Missing authority source',
  // Legacy names on older rows:
  engine_absence: 'Engine absence',
  negative_sentiment: 'Negative sentiment',
  inaccurate_claims: 'Inaccurate claims',
  weak_citations: 'Weak citations',
  source_gap: 'Source gap',
};

const ENGINE_SLUGS = Object.keys(ENGINE_LABELS);
const ENGINE_SLUG_RE = new RegExp(`\\b(${ENGINE_SLUGS.join('|')})\\b`, 'g');

/**
 * Swap raw engine slugs for their display labels ("gemini" -> "Gemini"). Purely
 * cosmetic: it changes no claim and no number, it just stops backend-authored
 * prose reading like a database column.
 */
function withEngineLabels(text: string): string {
  return text.replace(ENGINE_SLUG_RE, (slug) => ENGINE_LABELS[slug] ?? slug);
}

/**
 * The pattern classification the backend attached to an `engine_gap`, or null
 * when this gap has none (a row written before the classification existed).
 *
 * Read defensively: `supporting_data` is free-form JSON off the wire.
 */
export function engineGapPattern(gap: Gap): EngineGapPattern | null {
  if (gap.type !== 'engine_gap') return null;
  const data = gap.supporting_data;
  if (data == null || typeof data !== 'object') return null;
  const pattern = (data as { pattern?: unknown }).pattern;
  return pattern === 'not_retrieved'
    || pattern === 'retrieved_not_cited'
    || pattern === 'retrieved_not_named'
    || pattern === 'undetermined'
    ? pattern
    : null;
}

function humanizeGapDescription(gap: Gap): string {
  const desc = gap.description;
  const engineLabel = gap.engine ? (ENGINE_LABELS[gap.engine] ?? gap.engine) : null;

  // An `engine_gap` that carries a pattern classification already has a
  // backend-written explanation naming the RIGHT play, computed from the
  // lagging engine's own citation and retrieval rates over its web-searched
  // answers. Render it as written.
  //
  // The rules below reconstruct prose FROM prose with a regex, and the sentence
  // they used to append ("investigate engine-specific content strategies") is
  // discoverability advice — correct only for `not_retrieved`, and actively
  // misleading for an engine that is already citing the brand's domain and
  // simply isn't naming it. Regenerating advice on the client from a matched
  // substring also silently threw away the numbers the classification rests on.
  // The regex path survives only as the fallback for older rows that carry no
  // pattern at all.
  const pattern = engineGapPattern(gap);
  if (pattern != null) {
    return withEngineLabels(desc);
  }

  // Pattern: "Brand has X% mention rate on engine1 but only Y% on engine2"
  const mentionRatePattern = /has (\d+)% mention rate on (\w+) but only (\d+)% on (\w+)/i;
  const mentionMatch = desc.match(mentionRatePattern);
  if (mentionMatch) {
    const [, highRate, highEngine, lowRate, lowEngine] = mentionMatch;
    const highLabel = ENGINE_LABELS[highEngine] ?? highEngine;
    const lowLabel = ENGINE_LABELS[lowEngine] ?? lowEngine;
    if (lowRate === '0') {
      return `${lowLabel} doesn't mention the brand at all, while ${highLabel} mentions it in ${highRate}% of responses. This is a critical engine-specific visibility gap.`;
    }
    return `Mention rate on ${lowLabel} (${lowRate}%) lags far behind ${highLabel} (${highRate}%). Investigate engine-specific content strategies to close this gap.`;
  }

  // Pattern: "mention rate ... X% ... but ... Y%"
  const mentionGeneric = /mention.*?(\d+)%.*?but.*?(\d+)%/i;
  const mentionGenericMatch = desc.match(mentionGeneric);
  if (mentionGenericMatch) {
    return desc;
  }

  // Pattern: SOV decline
  if (gap.type === 'sov_decline') {
    return engineLabel
      ? `Share of voice is declining on ${engineLabel}. ${desc}`
      : `Share of voice is declining across engines. ${desc}`;
  }

  // Pattern: negative sentiment
  if (gap.type === 'negative_sentiment') {
    return engineLabel
      ? `Sentiment on ${engineLabel} is negative. ${desc}`
      : desc;
  }

  // Pattern: weak citations
  if (gap.type === 'weak_citations') {
    return engineLabel
      ? `${engineLabel} cites fewer authoritative sources for the brand. ${desc}`
      : desc;
  }

  return desc;
}

export function GapSummary({ tenantId, brandId, headlineTier }: GapSummaryProps) {
  const [gaps, setGaps] = useState<Gap[]>([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!tenantId || !brandId) return;

    let cancelled = false;
    setLoading(true);
    setUnavailable(false);

    apiGet<GapListResponse>(
      `/api/v1/tenants/${tenantId}/brands/${brandId}/gaps?limit=50`,
    )
      .then((res) => {
        if (!cancelled) setGaps(res.data);
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tenantId, brandId]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 rounded-md bg-gray-100" />
        ))}
      </div>
    );
  }

  if (unavailable) {
    return (
      <div className="flex h-32 flex-col items-center justify-center hub-plate--flat">
        <p className="text-sm font-medium text-gray-400">Gap analysis coming soon</p>
        <p className="mt-1 text-xs text-gray-400">
          Gap detection is being configured for this brand.
        </p>
      </div>
    );
  }

  if (gaps.length === 0) {
    // On a directional (thin) sample, the all-clear is not a validated finding —
    // it's the absence of evidence on too little data. Tone it down so it doesn't
    // read as reassurance the data can't support.
    //
    // Spec 248 (review P1-8): that intent is right and this keeps it — but
    // "toned down" had become AMBER: a check in an amber disc, amber text, and
    // an amber warning pill in the heading, for a state where nothing is wrong.
    // Good news is not a warning. The honest rendering is neutral — no green
    // reassurance, no amber alarm — with the small-sample caveat stated in the
    // caption as words. The directional case differs by what it SAYS, not by
    // a colour that implies a problem.
    const isDirectional = headlineTier === 'directional';
    const containerClass = 'flex h-32 flex-col items-center justify-center hub-plate--flat';
    const iconBgClass = 'hub-disc';
    const iconClass = 'h-5 w-5 text-[var(--hub-color-ink-muted)]';
    const headlineClass = 'mt-2 text-sm font-medium text-[var(--hub-color-ink)]';
    const subClass = 'mt-0.5 text-xs text-[var(--hub-color-ink-muted)]';
    return (
      <div className={containerClass}>
        <div className={`flex h-10 w-10 items-center justify-center rounded-full ${iconBgClass}`}>
          <svg className={iconClass} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
          </svg>
        </div>
        <p className={headlineClass}>
          {isDirectional ? 'No gaps detected yet — directional, small sample' : 'All visibility metrics look healthy'}
        </p>
        <p className={subClass}>
          {isDirectional
            ? 'Too little data to confirm — keep monitoring as more responses land.'
            : 'No gaps detected -- keep monitoring for changes.'}
        </p>
      </div>
    );
  }

  // Group gaps by severity
  const severityOrder: GapSeverity[] = ['critical', 'high', 'medium', 'low'];
  const grouped = new Map<GapSeverity, Gap[]>();
  for (const gap of gaps) {
    const list = grouped.get(gap.severity) ?? [];
    list.push(gap);
    grouped.set(gap.severity, list);
  }

  return (
    <div className="space-y-3" data-testid="gap-summary">
      {severityOrder.map((severity) => {
        const items = grouped.get(severity);
        if (!items || items.length === 0) return null;
        const badge = SEVERITY_BADGE[severity];
        return (
          <div key={severity} className="space-y-2">
            {items.map((gap) => {
              const typeLabel = TYPE_LABELS[gap.type] ?? gap.type;
              const borderClass = SEVERITY_BORDER[severity];
              const humanDesc = humanizeGapDescription(gap);
              const gapPattern = engineGapPattern(gap);
              return (
                <div
                  key={gap.id}
                  className={`border-b border-[var(--hub-color-line)] border-l-[3px] ${borderClass} p-5 transition-colors hover:bg-[var(--hub-color-surface-sunken)]`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`hub-chip ${badge.tone}`}>
                      {badge.label}
                    </span>
                    <span className="hub-tag">
                      {typeLabel}
                    </span>
                    {gap.engine && (
                      <span className="text-xs text-slate-400">
                        {ENGINE_LABELS[gap.engine] ?? gap.engine}
                      </span>
                    )}
                    {gap.prompt_category && (
                      <span className="text-xs text-slate-400">
                        / {gap.prompt_category}
                      </span>
                    )}
                  </div>
                  <p
                    className="mt-1.5 text-sm leading-relaxed text-slate-700"
                    data-testid={gapPattern != null ? 'gap-engine-pattern' : undefined}
                    data-gap-pattern={gapPattern ?? undefined}
                  >
                    {humanDesc}
                  </p>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
